import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    QuestionTypeFrontend,
    SessionQuestionEntityFrontend,
} from '../../../api/sessionQuestionsApi';
import { QuestionField, QuestionFieldProps } from '../fields/QuestionField';
import { TranslateFn } from '../hooks/useSessionFormI18n';

export interface FormSectionProps {
    questionType: QuestionTypeFrontend;
    questions: SessionQuestionEntityFrontend[];
    values: Record<number, unknown>;
    onValueChange: QuestionFieldProps['onValueChange'];
    onBlur: QuestionFieldProps['onBlur'];
    onImmediateChange: QuestionFieldProps['onImmediateChange'];
    onOpenListModal: QuestionFieldProps['onOpenListModal'];
    uploads: QuestionFieldProps['uploads'];
    onUpload: QuestionFieldProps['onUpload'];
    professionOptions: QuestionFieldProps['professionOptions'];
    onAddProfession: QuestionFieldProps['onAddProfession'];
    isAddingProfession: QuestionFieldProps['isAddingProfession'];
    t: TranslateFn;
}

const SECTION_KEY: Record<QuestionTypeFrontend, string> = {
    [QuestionTypeFrontend.LANGUAGE_SELECTION]: 'section_language_selection',
    [QuestionTypeFrontend.PROFESSION_SELECTION]: 'section_profession_selection',
    [QuestionTypeFrontend.CUSTOM_OCCUPATION_INPUT]: 'section_custom_occupation',
    [QuestionTypeFrontend.PERSONAL]: 'section_personal',
    [QuestionTypeFrontend.PROFESSION]: 'section_profession',
    [QuestionTypeFrontend.FINAL_PROFESSION_CONFIRMATION]: 'section_final_profession_confirmation',
    [QuestionTypeFrontend.CV_UPLOAD]: 'section_cv_upload',
    [QuestionTypeFrontend.ACTION]: 'section_action',
    [QuestionTypeFrontend.CLOSING]: 'section_closing',
};

export const FormSection: React.FC<FormSectionProps> = ({
    questionType,
    questions,
    values,
    onValueChange,
    onBlur,
    onImmediateChange,
    onOpenListModal,
    uploads,
    onUpload,
    professionOptions,
    onAddProfession,
    isAddingProfession,
    t,
}) => {
    const theme = useTheme();
    // Split into "needs attention" (no AI suggestion or rejected) vs "AI-
    // prefilled and waiting for confirmation". After running AI prefill the
    // worker typically only cares about the unfilled rows — the prefilled
    // ones live behind an expand toggle so they can still be reviewed and
    // edited without dominating the page.
    const { unfilled, prefilled } = useMemo(() => {
        const u: SessionQuestionEntityFrontend[] = [];
        const p: SessionQuestionEntityFrontend[] = [];
        for (const q of questions) {
            if (q.cvPrefilled) {
                p.push(q);
            } else {
                u.push(q);
            }
        }
        return { unfilled: u, prefilled: p };
    }, [questions]);

    const [showPrefilled, setShowPrefilled] = useState<boolean>(false);

    if (questions.length === 0) {
        return null;
    }

    const renderQuestionCard = (question: SessionQuestionEntityFrontend) => (
        <Paper
            key={question.id}
            elevation={1}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                // Soft tint on AI-prefilled rows so the worker visually
                // distinguishes "machine guessed" from "you wrote this".
                bgcolor: question.cvPrefilled
                    ? alpha(theme.palette.primary.main, 0.06)
                    : 'background.paper',
                borderLeft: question.cvPrefilled ? '3px solid' : undefined,
                borderColor: question.cvPrefilled ? 'primary.main' : undefined,
            }}
        >
            <Stack spacing={2}>
                <QuestionField
                    question={question}
                    value={values[question.id]}
                    onValueChange={onValueChange}
                    onBlur={onBlur}
                    onImmediateChange={onImmediateChange}
                    onOpenListModal={onOpenListModal}
                    uploads={uploads}
                    onUpload={onUpload}
                    professionOptions={professionOptions}
                    onAddProfession={onAddProfession}
                    isAddingProfession={isAddingProfession}
                    t={t}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                    {question.cvPrefilled && (
                        <Chip
                            size="small"
                            icon={<AutoFixHighIcon fontSize="small" />}
                            label={t('chip_suggested_by_ai')}
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {question.isRequiredAnswer && (
                        <Typography variant="caption" color="error.main">
                            {t('required')}
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );

    const toggleKey = prefilled.length === 1
        ? (showPrefilled ? 'prefilled_hide' : 'prefilled_show')
        : (showPrefilled ? 'prefilled_hide_plural' : 'prefilled_show_plural');

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                {t(SECTION_KEY[questionType] ?? questionType)}
            </Typography>
            <Stack spacing={2}>
                {unfilled.map(renderQuestionCard)}

                {prefilled.length > 0 && (
                    <Box>
                        <Button
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={showPrefilled ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setShowPrefilled((s) => !s)}
                            sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                            {t(toggleKey, { count: prefilled.length })}
                        </Button>
                        {showPrefilled && (
                            <Stack spacing={2} sx={{ mt: 1.5 }}>
                                {prefilled.map(renderQuestionCard)}
                            </Stack>
                        )}
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
